/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  createSlice,
  CreateSliceOptions,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers
} from "@reduxjs/toolkit";
import {setWizardOpen,} from "./reducers";
import {IMessage} from "../../../containers/pages/radar/SensorRadar/FileProcessingMessages";

export interface IWizardState {
  isWizardOpen: boolean
  refreshTableCount: number
  fileUploadMessages: IMessage[]
  fileUploadResponse: any
  fileUploadHasErrors: boolean
  fileDataHasStatements: boolean
  fileDataHasErrors: boolean
  fileDataHasWarnings: boolean
  dontAllowSubmit: string[]
  plotData: any[]
  cosmosData: any
  allStatementMessages: IMessage[][]
  allErrorMessages: IMessage[][]
  allWarningMessages: IMessage[][]
  metaData: any | null
}

export const baseInitialState = {
  isWizardOpen: false,
  refreshTableCount: 0,
  fileUploadMessages: [],
  fileUploadResponse: null,
  fileUploadHasErrors: false,
  fileDataHasStatements: false,
  fileDataHasErrors: false,
  fileDataHasWarnings: false,
  dontAllowSubmit: [],
  plotData: [],
  cosmosData: null,
  allStatementMessages: [],
  allErrorMessages: [],
  allWarningMessages: [],
  metaData: null
} as IWizardState

interface Ic extends CreateSliceOptions {
  extraReducers: any
}

const createSearchAppSlice = <Reducers extends SliceCaseReducers<IWizardState>>
({
   name = '',
   initialState,
   reducers,
   extraReducers,
 }: {
  name: string
  initialState?: IWizardState
  reducers?: ValidateSliceCaseReducers<IWizardState, Reducers>
  extraReducers?: any
}) => {
  // Setup a default state, and allow the function arg to change the defaults.
  initialState = {
    ...baseInitialState,
    ...initialState,
  };

  return createSlice({
    name,
    initialState,
    reducers: {
      // reusable reducers.
      setWizardOpen,
      refreshTable: (state) => void (state.refreshTableCount += 1),
      fileUploadAddMessage: (state, action: PayloadAction<IMessage>) => void state.fileUploadMessages.push(action.payload),
      fileUploadClearMessages: (state) => void (state.fileUploadMessages = []),
      setFileUploadResponse: (state, action: PayloadAction<any>) => void (state.fileUploadResponse = action.payload),
      setFileUploadHasErrors: (state, action: PayloadAction<boolean>) => void (state.fileUploadHasErrors = action.payload),
      setFileDataHasStatements: (state, action: PayloadAction<boolean>) => void (state.fileDataHasStatements = action.payload),
      setFileDataHasErrors: (state, action: PayloadAction<boolean>) => void (state.fileDataHasErrors = action.payload),
      setFileDataHasWarnings: (state, action: PayloadAction<boolean>) => void (state.fileDataHasWarnings = action.payload),
      setAllStatementMessages: (state, action: PayloadAction<IMessage[][]>) => void (state.allStatementMessages = action.payload),
      setAllErrorMessages: (state, action: PayloadAction<IMessage[][]>) => void (state.allErrorMessages = action.payload),
      setAllWarningMessages: (state, action: PayloadAction<IMessage[][]>) => void (state.allWarningMessages = action.payload),
      setDontAllowSubmit: (state, action: PayloadAction<string[]>) => void (state.dontAllowSubmit = action.payload),
      setWizardMetaData: (state, action: PayloadAction<any>) => void (state.metaData = action.payload),
      appendStepXErrorMessages: {
        reducer: (state, action: PayloadAction<IMessage[], string, {
          index: number
        }>) => {},
        prepare: (payload: IMessage[], index: number) => ({payload, meta: {index}})
      },
      clearState: (state) => {
        const init: IWizardState = {...initialState} as IWizardState
        const initcount = state.refreshTableCount + 1
        init.refreshTableCount = initcount
        return init
      },
      // custom reducers specific to this slice.
      ...reducers,
    },
    // extraReducers are global reducers that apply to all slices in the store.
    extraReducers,
  });
};

export const radarSlice = createSearchAppSlice({
  name: "radar",
});

export const cameraSlice = createSearchAppSlice({
  name: "camera",
});

export const ultrasonicSlice = createSearchAppSlice({
  name: "ultrasonic",
});
export const lidarSlice = createSearchAppSlice({
  name: "lidar",
});
export const vehicleProgramSlice = createSearchAppSlice({
  name: "vehicleProgram",
});
